import {ScrollView} from "react-native";
import {EventInviteRequest, EventInviteCandidates} from "@/lib/types";
import {useLocalSearchParams, useRouter} from "expo-router";
import {dataRetryPolicy, fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import Page from "@/lib/pages/Page";
import ErrorPage from "@/lib/pages/ErrorPage";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import SpinnerPage from "@/lib/pages/SpinnerPage";
import {Color, rowColor} from "@/lib/styles";
import Header from "@/lib/components/Header";
import Nav from "@/lib/components/Nav";
import ActionButton from "@/lib/components/ActionButton";
import AvatarRow from "@/lib/components/AvatarRow";
import {useState} from "react";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Message from "@/lib/components/Message";
import {useSafeAreaInsets} from "react-native-safe-area-context";
import {useEvent} from "@/lib/providers/EventProvider";
import Table from "@/lib/components/Table";

export default function InvitePage() {
    const {event_id} = useLocalSearchParams();
    const {session} = useSession();
    const inset = useSafeAreaInsets();
    const queryClient = useQueryClient();
    const router = useRouter();

    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const {event, eventLoading, eventError} = useEvent();

    const inviteQuery = useQuery<EventInviteCandidates>({
        queryKey: ['event', event_id, 'invite', session],
        queryFn: () => fetchBackend(`/event/${event_id}/invite`, "GET", session),
        retry: dataRetryPolicy,
    });

    if (eventError) {
        return <ErrorPage error={eventError}/>
    } else if (inviteQuery.error) {
        return <ErrorPage error={inviteQuery.error}/>
    } else if (eventLoading || inviteQuery.isPending || !event || !inviteQuery.data) {
        return <SpinnerPage/>
    }

    async function submitInvites() {
        setBusy(true);
        const payload = new EventInviteRequest(selectedUsers, selectedGroups);
        try {
            await fetchBackendNoContent(`/event/${event_id}/invite`, "POST", session, payload);
            await queryClient.invalidateQueries({queryKey: ['event', event_id, session]});
            router.dismiss();
        } catch (err) {
            setError(err as Error);
        } finally {
            setBusy(false);
        }
    }

    function toggleUser(userID: number) {
        if (selectedUsers.includes(userID)) {
            setSelectedUsers(selectedUsers.filter(p => userID !== p));
        } else {
            setSelectedUsers([...selectedUsers, userID]);
        }
    }

    function toggleGroup(groupID: string) {
        if (selectedGroups.includes(groupID)) {
            setSelectedGroups(selectedGroups.filter(g => groupID !== g));
        } else {
            setSelectedGroups([...selectedGroups, groupID]);
        }
    }

    const candidate = inviteQuery.data;
    const totalInvites = selectedUsers.length + selectedGroups.length;

    return (
        <Page>
            <Header icon={"event"} title={`Inviterer til ${event.title}`}/>
            <ScrollView focusable={false}
                        indicatorStyle={"white"}
                        contentContainerStyle={{gap: 12}}
            >
                <Header title={"Bekjente"} icon={"relation"} marginTop={8}/>
                {candidate.users.length === 0 && <Message text={"Alle dine bekjente er allerede invitert til arrangementet."}/>}
                {candidate.users.length > 0 && (
                    <Table>
                        {candidate.users.map((avatar, index) => (
                            <AvatarRow key={avatar.id}
                                       avatar={avatar}
                                       badgeColor={avatar.color}
                                       color={rowColor(Color.Container, index)}
                                       contrastColor={avatar.contrast_color}
                                       onPress={() => toggleUser(avatar.id)}
                                       checked={selectedUsers.includes(avatar.id)}
                            />
                        ))}
                    </Table>
                )}
                <Header title={"Samfunn"} icon={"participants"}/>
                {candidate.groups.length === 0 && <Message text={"Alle samfunn hvor du er medlem er allerede invitert til arrangementet."}/>}
                {candidate.groups.length > 0 && (
                    <Table>
                        {candidate.groups.map((avatar, index) => (
                            <AvatarRow key={avatar.id}
                                       avatar={avatar}
                                       badgeColor={avatar.color}
                                       color={rowColor(Color.Container, index)}
                                       contrastColor={avatar.contrast_color}
                                       onPress={() => toggleGroup(avatar.id)}
                                       checked={selectedGroups.includes(avatar.id)}
                            />
                        ))}
                    </Table>
                )}
            </ScrollView>

            {error && <ErrorMessage error={error}/>}

            <Nav style={{marginBottom: 8 + inset.bottom}}>
                <ActionButton onPress={submitInvites}
                              title={"Send invitasjoner ( " + totalInvites + " )"}
                              busy={busy}
                />
            </Nav>
        </Page>
    )
}